import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Send, Loader2, Crown, Zap, Sparkles, SkipForward, RotateCcw, Trash2, Pencil, X, Check, MoreVertical, UserPlus, Plus, User, AlertTriangle, ChevronDown, Lock, Palette, BookHeart } from 'lucide-react';
import AnimatedAvatar from '../components/AnimatedAvatar';
import UltimateTag from '../components/UltimateTag';
import { getCharacterTheme } from '../utils/characterTheme';
import { fallbackAvatarUrl } from '../utils/fallbackAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODEL_COLORS = {
  standard: '#A1A1AA',
  passion: '#F59E0B',
  inferno: '#EF4444',
  saga: '#A855F7',
  apex: '#E11D48',
};

const BUBBLE_COLORS = [
  { id: 'default', color: 'rgba(255,255,255,0.1)', label: 'Default' },
  { id: 'red', color: 'rgba(225,29,72,0.25)', label: 'Crimson' },
  { id: 'blue', color: 'rgba(59,130,246,0.25)', label: 'Blue' },
  { id: 'purple', color: 'rgba(168,85,247,0.25)', label: 'Purple' },
  { id: 'green', color: 'rgba(34,197,94,0.25)', label: 'Green' },
  { id: 'amber', color: 'rgba(245,158,11,0.25)', label: 'Amber' },
  { id: 'pink', color: 'rgba(236,72,153,0.25)', label: 'Pink' },
  { id: 'teal', color: 'rgba(20,184,166,0.25)', label: 'Teal' },
];

const PREMIUM_GRADIENTS = [
  { id: 'grad-fire', style: 'linear-gradient(135deg, rgba(225,29,72,0.3), rgba(245,158,11,0.3))', label: 'Fire' },
  { id: 'grad-ocean', style: 'linear-gradient(135deg, rgba(59,130,246,0.3), rgba(20,184,166,0.3))', label: 'Ocean' },
  { id: 'grad-galaxy', style: 'linear-gradient(135deg, rgba(168,85,247,0.3), rgba(236,72,153,0.3))', label: 'Galaxy' },
  { id: 'grad-sunset', style: 'linear-gradient(135deg, rgba(245,158,11,0.3), rgba(236,72,153,0.3))', label: 'Sunset' },
  { id: 'grad-neon', style: 'linear-gradient(135deg, rgba(34,197,94,0.3), rgba(59,130,246,0.3))', label: 'Neon' },
  { id: 'grad-midnight', style: 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(225,29,72,0.3))', label: 'Midnight' },
];

function getBubbleStyle(colorId, isPremium) {
  if (colorId?.startsWith('grad-')) {
    const grad = PREMIUM_GRADIENTS.find(g => g.id === colorId);
    return grad ? { background: grad.style } : {};
  }
  const c = BUBBLE_COLORS.find(b => b.id === colorId);
  return c ? { backgroundColor: c.color } : {};
}

// Parse message content: *action text* becomes faded italic, regular text is bright dialogue
function MessageContent({ content, isUser }) {
  const parts = content.split(/(\*[^*]+\*)/g);
  return (
    <div className="text-sm whitespace-pre-wrap leading-relaxed">
      {parts.map((part, i) => {
        if (part.startsWith('*') && part.endsWith('*')) {
          const actionText = part.slice(1, -1);
          return (
            <span key={i} className="italic" style={{ color: isUser ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.4)' }}>
              {actionText}
            </span>
          );
        }
        return <span key={i} className="text-white font-medium">{part}</span>;
      })}
    </div>
  );
}

export default function Chat() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [userTier, setUserTier] = useState('free');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [activeMenu, setActiveMenu] = useState(null);
  const [showPersonaSheet, setShowPersonaSheet] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const [characterData, setCharacterData] = useState(null);
  const [selectedModel, setSelectedModel] = useState('standard');
  const [availableModels, setAvailableModels] = useState([]);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [chatTheme, setChatTheme] = useState({ userBubble: 'default', charBubble: 'default' });
  const [showMemoryPaywall, setShowMemoryPaywall] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load saved chat theme
  useEffect(() => {
    const saved = localStorage.getItem(`chat-theme-${id}`);
    if (saved) setChatTheme(JSON.parse(saved));
  }, [id]);

  const saveChatTheme = (theme) => {
    setChatTheme(theme);
    localStorage.setItem(`chat-theme-${id}`, JSON.stringify(theme));
  };

  useEffect(() => {
    fetchConversation();
    fetchMessages();
    fetchUserTier();
    fetchModels();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close menu/picker on click outside
  useEffect(() => {
    if (activeMenu || showModelPicker) {
      const handler = (e) => {
        if (!e.target.closest('[data-testid="model-picker-dropdown"]') && !e.target.closest('[data-testid="model-selector-btn"]')) {
          setShowModelPicker(false);
        }
        setActiveMenu(null);
      };
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }
  }, [activeMenu, showModelPicker]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchUserTier = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/user/subscription`, { withCredentials: true });
      setUserTier(data.tier);
    } catch { setUserTier('free'); }
  };

  const fetchModels = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chat/models`, { withCredentials: true });
      setAvailableModels(data.models || []);
    } catch (err) { console.error('Error fetching models:', err); }
  };

  const fetchConversation = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/conversations`, { withCredentials: true });
      const conv = data.find(c => c.id === id);
      if (conv) {
        setConversation(conv);
        // Fetch character details for creator info
        try {
          const charRes = await axios.get(`${API_URL}/api/characters/${conv.character_id}`, { withCredentials: true });
          setCharacterData(charRes.data);
        } catch {}
      }
    } catch (err) { console.error('Error fetching conversation:', err); }
  };

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/conversations/${id}/messages`, { withCredentials: true });
      setMessages(data);
    } catch {
      navigate('/chats');
    } finally { setLoading(false); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || forwarding) return;

    const userMessage = { id: `temp-${Date.now()}`, role: 'user', content: newMessage, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setSending(true);

    try {
      const { data } = await axios.post(`${API_URL}/api/conversations/${id}/messages`, { content: userMessage.content, model_id: selectedModel }, { withCredentials: true });
      setMessages(prev => [...prev.filter(m => m.id !== userMessage.id), { ...userMessage, id: `user-${Date.now()}` }, data]);
    } catch {
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const forwardChat = async () => {
    if (sending || forwarding) return;
    setForwarding(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/conversations/${id}/forward`, { model_id: selectedModel }, { withCredentials: true });
      setMessages(prev => [...prev, data]);
    } catch (err) { console.error('Forward error:', err); }
    finally { setForwarding(false); }
  };

  const restartChat = async () => {
    if (!window.confirm('Restart this conversation? All messages will be cleared.')) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/conversations/${id}/restart`, {}, { withCredentials: true });
      setMessages([data.greeting]);
    } catch (err) { console.error('Restart error:', err); }
  };

  const deleteConversation = async () => {
    if (!window.confirm('Delete this entire conversation?')) return;
    try {
      await axios.delete(`${API_URL}/api/conversations/${id}`, { withCredentials: true });
      navigate('/chats');
    } catch (err) { console.error('Delete error:', err); }
  };

  const editMessage = async (msgId) => {
    if (!editText.trim()) return;
    try {
      await axios.put(`${API_URL}/api/conversations/${id}/messages/${msgId}`, { content: editText }, { withCredentials: true });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content: editText, edited: true } : m));
      setEditingId(null);
      setEditText('');
    } catch (err) { console.error('Edit error:', err); }
  };

  const deleteMessage = async (msgId) => {
    try {
      await axios.delete(`${API_URL}/api/conversations/${id}/messages/${msgId}`, { withCredentials: true });
      setMessages(prev => prev.filter(m => m.id !== msgId));
      setActiveMenu(null);
    } catch (err) { console.error('Delete message error:', err); }
  };

  const startEdit = (msg) => {
    setEditingId(msg.id);
    setEditText(msg.content);
    setActiveMenu(null);
  };

  const [personaLimit, setPersonaLimit] = useState({ max: 3, tier: 'free' });

  const fetchPersonas = async () => {
    setLoadingPersonas(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/personas`, { withCredentials: true });
      // New shape: { personas: [...], count, max_personas, tier }
      const list = Array.isArray(data) ? data : (data.personas || []);
      setPersonas(list);
      if (!Array.isArray(data)) {
        setPersonaLimit({ max: data.max_personas || 3, tier: data.tier || 'free' });
      }
    } catch (err) { console.error('Personas error:', err); }
    finally { setLoadingPersonas(false); }
  };

  const openPersonaSheet = () => {
    setShowPersonaSheet(true);
    fetchPersonas();
  };

  const setDefaultPersona = async (personaId) => {
    try {
      await axios.post(`${API_URL}/api/personas/${personaId}/default`, {}, { withCredentials: true });
      setPersonas(prev => prev.map(p => ({ ...p, is_default: p.id === personaId })));
    } catch (err) { console.error('Set default error:', err); }
  };

  const removeDefault = async (personaId) => {
    try {
      // Update to unset default by updating with is_default false
      const persona = personas.find(p => p.id === personaId);
      if (persona) {
        await axios.put(`${API_URL}/api/personas/${personaId}`, { ...persona, is_default: false }, { withCredentials: true });
        setPersonas(prev => prev.map(p => p.id === personaId ? { ...p, is_default: false } : p));
      }
    } catch (err) { console.error('Remove default error:', err); }
  };

  const deletePersona = async (personaId) => {
    try {
      await axios.delete(`${API_URL}/api/personas/${personaId}`, { withCredentials: true });
      setPersonas(prev => prev.filter(p => p.id !== personaId));
    } catch (err) { console.error('Delete persona error:', err); }
  };

  const editLastMessage = () => {
    const userMsgs = messages.filter(m => m.role === 'user');
    if (userMsgs.length > 0) {
      const last = userMsgs[userMsgs.length - 1];
      startEdit(last);
      // Scroll to the message
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getTierBadge = () => {
    switch (userTier) {
      case 'ultimate':
        return (
          <div className="flex items-center gap-1 px-2 py-0.5 bg-[#E11D48]/20 rounded-full">
            <Zap className="w-3 h-3 text-[#E11D48]" />
            <span className="text-[10px] font-medium text-[#E11D48]">Ultimate</span>
          </div>
        );
      case 'premium':
        return (
          <div className="flex items-center gap-1 px-2 py-0.5 bg-[#F59E0B]/20 rounded-full">
            <Crown className="w-3 h-3 text-[#F59E0B]" />
            <span className="text-[10px] font-medium text-[#F59E0B]">Premium</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1 px-2 py-0.5 bg-white/10 rounded-full">
            <Sparkles className="w-3 h-3 text-[#A1A1AA]" />
            <span className="text-[10px] font-medium text-[#A1A1AA]">Free</span>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#050505]">
        <Loader2 className="w-8 h-8 text-[#E11D48] animate-spin" />
      </div>
    );
  }

  const defaultAvatar = fallbackAvatarUrl(conversation?.character_name || '');
  const backgroundImage = conversation?.character_avatar || defaultAvatar;
  const theme = getCharacterTheme(conversation);

  return (
    <div
      className="h-screen flex flex-col relative"
      style={{
        '--accent': theme.accent,
        '--accent-shadow': theme.shadow,
      }}
    >
      {/* Theme gradient base + character image blurred on top, then dark overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background: `linear-gradient(180deg, ${theme.from} 0%, ${theme.via} 55%, ${theme.to} 100%)`,
        }}
      />
      <div
        className="absolute inset-0 z-0 opacity-50"
        style={{ backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-[3px]" />
      </div>
      {/* Accent glow blob top-right */}
      <div
        className="absolute -top-32 -right-20 w-72 h-72 rounded-full blur-3xl z-0 pointer-events-none"
        style={{ background: theme.accent, opacity: 0.10 }}
      />

      {/* Chat Header */}
      <div
        className="relative z-10 flex items-center justify-between px-3 pb-2.5 bg-black/50 backdrop-blur-md border-b border-white/10"
        style={{ paddingTop: 'max(0.625rem, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center gap-2.5">
          <button onClick={() => navigate('/chats')} className="p-1.5 text-white hover:bg-white/10 rounded-full" data-testid="back-to-chats">
            <ArrowLeft className="w-5 h-5" />
          </button>
          {conversation && (
            <>
              <AnimatedAvatar
                src={conversation.character_avatar || defaultAvatar}
                alt={conversation.character_name}
                className="w-9 h-9 object-cover border-2 border-white/20"
              />
              <div>
                <h2 className="font-bold text-white text-sm leading-tight" data-testid="chat-character-name">
                  {conversation.character_name}
                </h2>
                {getTierBadge()}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Memory Vault Button — gated to Premium/Ultimate */}
          <button
            onClick={() => {
              if (userTier === 'free') {
                setShowMemoryPaywall(true);
              } else {
                navigate('/memory');
              }
            }}
            className="p-2 rounded-full border border-[#E11D48]/30 bg-[#E11D48]/10 hover:scale-105 transition-all relative"
            title={userTier === 'free' ? 'Permanent Memory — Premium feature' : 'Memory Vault — what your characters remember'}
            data-testid="memory-vault-btn"
          >
            <BookHeart className="w-4 h-4 text-[#E11D48]" />
            {userTier === 'free' && (
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#F59E0B] flex items-center justify-center">
                <Lock className="w-2 h-2 text-black" />
              </span>
            )}
          </button>
          {/* Model Selector Button */}
          <button
            onClick={() => setShowModelPicker(!showModelPicker)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-all hover:scale-105"
            style={{
              borderColor: `${MODEL_COLORS[selectedModel] || '#A1A1AA'}50`,
              background: `${MODEL_COLORS[selectedModel] || '#A1A1AA'}15`,
            }}
            data-testid="model-selector-btn"
          >
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black"
              style={{ background: MODEL_COLORS[selectedModel] || '#A1A1AA', color: '#000' }}
            >
              {availableModels.find(m => m.id === selectedModel)?.icon || 'S'}
            </span>
            <span className="text-xs font-medium text-white hidden sm:inline">
              {availableModels.find(m => m.id === selectedModel)?.name || 'Standard'}
            </span>
            <ChevronDown className="w-3 h-3 text-white/60" />
          </button>
        </div>
      </div>

      {/* Model Picker Dropdown */}
      {showModelPicker && (
        <div className="absolute top-[52px] right-3 z-50 w-56 bg-[#1A1D24] border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" data-testid="model-picker-dropdown">
          <div className="px-3 py-2 border-b border-white/10">
            <p className="text-[10px] font-medium text-[#71717A] uppercase tracking-wider">Select Model</p>
          </div>
          {['free', 'premium', 'ultimate'].map(tier => {
            const tierModels = availableModels.filter(m => m.tier === tier);
            if (tierModels.length === 0) return null;
            return (
              <div key={tier}>
                <div className="px-3 pt-2 pb-1">
                  <p className="text-[9px] font-bold uppercase tracking-widest" style={{
                    color: tier === 'ultimate' ? '#E11D48' : tier === 'premium' ? '#F59E0B' : '#71717A'
                  }}>
                    {tier}
                  </p>
                </div>
                {tierModels.map(model => (
                  <button
                    key={model.id}
                    onClick={() => {
                      if (!model.locked) {
                        setSelectedModel(model.id);
                        setShowModelPicker(false);
                      }
                    }}
                    disabled={model.locked}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 transition-colors ${
                      model.locked
                        ? 'opacity-40 cursor-not-allowed'
                        : selectedModel === model.id
                          ? 'bg-white/10'
                          : 'hover:bg-white/5'
                    }`}
                    data-testid={`model-option-${model.id}`}
                  >
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-black flex-shrink-0"
                      style={{
                        background: model.locked ? '#333' : model.color,
                        color: model.locked ? '#666' : '#000',
                      }}
                    >
                      {model.icon}
                    </span>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-white truncate">{model.name}</p>
                      <p className="text-[10px] text-[#71717A] truncate">{model.description}</p>
                    </div>
                    {model.locked && <Lock className="w-3.5 h-3.5 text-[#555] flex-shrink-0" />}
                    {selectedModel === model.id && !model.locked && (
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: model.color }} />
                    )}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Messages */}
      <div className="relative z-10 flex-1 overflow-y-auto px-3 py-3">
        <div className="max-w-3xl mx-auto space-y-3">
          {/* Creator Credit & AI Disclaimer */}
          {characterData && (
            <div className="text-center space-y-1 pb-2" data-testid="chat-disclaimer">
              <p className="text-xs text-white/70 inline-flex items-center gap-2 flex-wrap justify-center">
                This character was created by{' '}
                <button
                  onClick={() => navigate(`/user/${characterData.creator_id}`)}
                  className="text-[#E11D48] font-semibold hover:underline"
                  data-testid="creator-link"
                >
                  @{characterData.creator_name}
                </button>
                {characterData.creator_ultimate_tag && <UltimateTag tag={characterData.creator_ultimate_tag} size="xs" />}
              </p>
              <p className="text-[10px] text-white/40 leading-snug">
                All responses are AI-generated and fictional. All characters on the platform are presumed to be adults (aged 18 and above).
              </p>
            </div>
          )}
          {messages.map((message, index) => (
            <div key={message.id} className={`message-enter flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} group`} style={{ animationDelay: `${index * 0.03}s` }}>
              {/* AI avatar */}
              {message.role === 'assistant' && conversation && (
                <img src={conversation.character_avatar || defaultAvatar} alt="" className="w-7 h-7 rounded-full object-cover mr-2 flex-shrink-0 border border-white/20 mt-1" />
              )}

              <div className="relative max-w-[80%]">
                {/* Message bubble */}
                {editingId === message.id ? (
                  <div className="flex gap-2 items-center bg-black/80 rounded-2xl px-3 py-2 border border-[#E11D48]/30">
                    <input
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="flex-1 bg-transparent text-white text-sm outline-none"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter') editMessage(message.id); if (e.key === 'Escape') setEditingId(null); }}
                      data-testid="edit-message-input"
                    />
                    <button onClick={() => editMessage(message.id)} className="text-green-500 hover:text-green-400" data-testid="confirm-edit-btn">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-[#71717A] hover:text-white">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    className={`px-4 py-3 ${
                      message.role === 'user'
                        ? 'rounded-2xl rounded-br-sm'
                        : 'backdrop-blur-sm rounded-2xl rounded-bl-sm border border-white/10'
                    }`}
                    style={
                      message.role === 'user'
                        ? (chatTheme.userBubble !== 'default' ? { ...getBubbleStyle(chatTheme.userBubble), border: '1px solid rgba(255,255,255,0.1)' } : { backgroundColor: theme.accent })
                        : (chatTheme.charBubble !== 'default' ? { ...getBubbleStyle(chatTheme.charBubble), border: '1px solid rgba(255,255,255,0.1)' } : { backgroundColor: 'rgba(0,0,0,0.7)', borderLeft: `2px solid ${theme.accent}55` })
                    }
                    data-testid={`message-${message.role}-${index}`}
                  >
                    {message.role === 'assistant' && conversation && index === 0 && (
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs text-[#E11D48] font-medium">{conversation.character_name}</p>
                      </div>
                    )}
                    <MessageContent content={message.content} isUser={message.role === 'user'} />
                    {message.edited && <span className="text-[9px] text-white/40 mt-1 block">edited</span>}
                  </div>
                )}

                {/* User message actions (3-dot menu) */}
                {message.role === 'user' && editingId !== message.id && (
                  <div className="absolute -left-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === message.id ? null : message.id); }}
                      className="p-1 text-white/40 hover:text-white bg-black/50 rounded-full"
                      data-testid={`msg-menu-${index}`}
                    >
                      <MoreVertical className="w-3.5 h-3.5" />
                    </button>
                    {activeMenu === message.id && (
                      <div className="absolute right-0 top-full mt-1 bg-[#1A1D24] border border-white/10 rounded-lg shadow-xl overflow-hidden z-20 min-w-[120px]" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => startEdit(message)} className="w-full px-3 py-2 text-left text-xs text-white hover:bg-white/10 flex items-center gap-2" data-testid={`edit-msg-${index}`}>
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button onClick={() => deleteMessage(message.id)} className="w-full px-3 py-2 text-left text-xs text-[#E11D48] hover:bg-[#E11D48]/10 flex items-center gap-2" data-testid={`delete-msg-${index}`}>
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {(sending || forwarding) && (
            <div className="flex justify-start">
              {conversation && (
                <img src={conversation.character_avatar || defaultAvatar} alt="" className="w-7 h-7 rounded-full object-cover mr-2 border border-white/20" />
              )}
              <div className="bg-black/70 backdrop-blur-sm rounded-2xl rounded-bl-sm border border-white/10 px-4 py-3">
                <div className="flex items-center gap-1">
                  <div className="typing-dot w-2 h-2 bg-[#E11D48] rounded-full" />
                  <div className="typing-dot w-2 h-2 bg-[#E11D48] rounded-full" />
                  <div className="typing-dot w-2 h-2 bg-[#E11D48] rounded-full" />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input + Action Bar */}
      <div className="relative z-10 bg-black/50 backdrop-blur-md border-t border-white/10 p-3 pb-4">
        <form onSubmit={sendMessage} className="max-w-3xl mx-auto flex gap-2 mb-3">
          <Input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={`Message ${conversation?.character_name || 'character'}...`}
            disabled={sending || forwarding}
            className="flex-1 bg-white/10 border-white/10 text-white placeholder:text-white/50 rounded-full px-5 focus:border-[#E11D48] focus:ring-[#E11D48]"
            data-testid="message-input"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending || forwarding}
            className="p-3 bg-[#E11D48] hover:bg-[#BE123C] disabled:opacity-50 text-white rounded-full transition-colors"
            data-testid="send-message-button"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </form>

        {/* Action Toolbar */}
        <div className="max-w-3xl mx-auto flex items-center justify-around pb-16">
          <button
            onClick={restartChat}
            disabled={sending || forwarding}
            className="flex flex-col items-center gap-1 p-2 text-white/50 hover:text-[#F59E0B] transition-colors disabled:opacity-30"
            data-testid="restart-chat-btn"
          >
            <RotateCcw className="w-5 h-5" />
            <span className="text-[10px]">Restart</span>
          </button>

          <button
            onClick={editLastMessage}
            disabled={sending || forwarding || messages.filter(m => m.role === 'user').length === 0}
            className="flex flex-col items-center gap-1 p-2 text-white/50 hover:text-[#F59E0B] transition-colors disabled:opacity-30"
            data-testid="edit-last-msg-btn"
          >
            <Pencil className="w-5 h-5" />
            <span className="text-[10px]">Edit</span>
          </button>

          <button
            onClick={forwardChat}
            disabled={sending || forwarding}
            className="flex flex-col items-center gap-1 p-2 text-white/50 hover:text-[#E11D48] transition-colors disabled:opacity-30"
            data-testid="forward-btn"
          >
            {forwarding ? <Loader2 className="w-5 h-5 animate-spin" /> : <SkipForward className="w-5 h-5" />}
            <span className="text-[10px]">Continue</span>
          </button>

          <button
            onClick={openPersonaSheet}
            className="flex flex-col items-center gap-1 p-2 text-white/50 hover:text-[#F59E0B] transition-colors"
            data-testid="create-persona-btn"
          >
            <UserPlus className="w-5 h-5" />
            <span className="text-[10px]">Persona</span>
          </button>

          <button
            onClick={deleteConversation}
            disabled={sending || forwarding}
            className="flex flex-col items-center gap-1 p-2 text-white/50 hover:text-[#E11D48] transition-colors disabled:opacity-30"
            data-testid="delete-chat-toolbar-btn"
          >
            <Trash2 className="w-5 h-5" />
            <span className="text-[10px]">Delete</span>
          </button>

          <button
            onClick={() => setShowCustomize(true)}
            className="flex flex-col items-center gap-1 p-2 text-white/50 hover:text-[#F59E0B] transition-colors"
            data-testid="customize-chat-btn"
          >
            <Palette className="w-5 h-5" />
            <span className="text-[10px]">Theme</span>
          </button>
        </div>
      </div>

      {/* Chat Theme Customization */}
      {showCustomize && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={() => setShowCustomize(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg bg-[#0F1115] rounded-t-2xl border-t border-white/10 max-h-[75vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mt-3" />
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <div>
                <h3 className="text-white font-bold font-['Unbounded'] text-base">Chat Theme</h3>
                <p className="text-xs text-[#71717A]">Customise bubble colours</p>
              </div>
              <button onClick={() => setShowCustomize(false)} className="p-2 text-[#71717A] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-4 space-y-5">
              {/* Your Bubble Color */}
              <div>
                <p className="text-sm font-medium text-white mb-2">Your Bubble</p>
                <div className="flex flex-wrap gap-2">
                  {BUBBLE_COLORS.map(c => (
                    <button
                      key={c.id}
                      onClick={() => saveChatTheme({ ...chatTheme, userBubble: c.id })}
                      className={`w-10 h-10 rounded-xl border-2 transition-all ${chatTheme.userBubble === c.id ? 'border-white scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c.id === 'default' ? '#E11D48' : c.color }}
                      title={c.label}
                      data-testid={`user-color-${c.id}`}
                    />
                  ))}
                </div>
                {/* Premium Gradients */}
                <div className="mt-3">
                  <p className="text-xs text-[#F59E0B] mb-2 flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Premium Gradients
                    {userTier === 'free' && <Lock className="w-3 h-3 text-[#71717A] ml-1" />}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {PREMIUM_GRADIENTS.map(g => (
                      <button
                        key={g.id}
                        onClick={() => userTier !== 'free' && saveChatTheme({ ...chatTheme, userBubble: g.id })}
                        disabled={userTier === 'free'}
                        className={`w-10 h-10 rounded-xl border-2 transition-all ${chatTheme.userBubble === g.id ? 'border-white scale-110' : 'border-transparent'} ${userTier === 'free' ? 'opacity-40' : ''}`}
                        style={{ background: g.style }}
                        title={g.label}
                        data-testid={`user-grad-${g.id}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Character Bubble Color */}
              <div>
                <p className="text-sm font-medium text-white mb-2">Character Bubble</p>
                <div className="flex flex-wrap gap-2">
                  {BUBBLE_COLORS.map(c => (
                    <button
                      key={c.id}
                      onClick={() => saveChatTheme({ ...chatTheme, charBubble: c.id })}
                      className={`w-10 h-10 rounded-xl border-2 transition-all ${chatTheme.charBubble === c.id ? 'border-white scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c.id === 'default' ? 'rgba(0,0,0,0.7)' : c.color }}
                      title={c.label}
                      data-testid={`char-color-${c.id}`}
                    />
                  ))}
                </div>
                {/* Premium Gradients */}
                <div className="mt-3">
                  <p className="text-xs text-[#F59E0B] mb-2 flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Premium Gradients
                    {userTier === 'free' && <Lock className="w-3 h-3 text-[#71717A] ml-1" />}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {PREMIUM_GRADIENTS.map(g => (
                      <button
                        key={g.id}
                        onClick={() => userTier !== 'free' && saveChatTheme({ ...chatTheme, charBubble: g.id })}
                        disabled={userTier === 'free'}
                        className={`w-10 h-10 rounded-xl border-2 transition-all ${chatTheme.charBubble === g.id ? 'border-white scale-110' : 'border-transparent'} ${userTier === 'free' ? 'opacity-40' : ''}`}
                        style={{ background: g.style }}
                        title={g.label}
                        data-testid={`char-grad-${g.id}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Reset */}
              <button
                onClick={() => saveChatTheme({ userBubble: 'default', charBubble: 'default' })}
                className="w-full py-2.5 text-sm text-[#71717A] hover:text-white bg-white/5 rounded-xl transition-colors"
                data-testid="reset-theme-btn"
              >
                Reset to Default
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Persona Bottom Sheet */}
      {showPersonaSheet && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={() => setShowPersonaSheet(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-lg bg-[#0F1115] rounded-t-2xl border-t border-white/10 max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-white/20 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2">
              <div>
                <h3 className="font-bold text-white text-base">My Persona</h3>
                <p className="text-[10px] text-[#71717A] mt-0.5" data-testid="persona-limit-label">
                  {personas.length}/{personaLimit.max} used
                  {personaLimit.tier === 'free' && personas.length >= personaLimit.max && (
                    <span className="ml-1.5 text-[#F59E0B]">· Upgrade for 10</span>
                  )}
                </p>
              </div>
              <button
                onClick={() => {
                  if (personas.length >= personaLimit.max) {
                    if (personaLimit.tier === 'free') {
                      navigate('/subscription');
                    } else {
                      alert(`You've reached the maximum of ${personaLimit.max} personas.`);
                    }
                    return;
                  }
                  navigate('/persona/create');
                }}
                disabled={personas.length >= personaLimit.max && personaLimit.tier !== 'free'}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E11D48] rounded-full text-white text-xs font-medium disabled:opacity-50"
                data-testid="create-persona-from-sheet"
              >
                <Plus className="w-3.5 h-3.5" /> New
              </button>
            </div>

            {/* Persona List */}
            <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-3">
              {loadingPersonas ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 text-[#E11D48] animate-spin" />
                </div>
              ) : personas.length === 0 ? (
                <div className="text-center py-8">
                  <User className="w-10 h-10 text-[#71717A] mx-auto mb-3" />
                  <p className="text-sm text-[#A1A1AA] mb-1">No personas yet</p>
                  <p className="text-xs text-[#71717A]">Create a persona so characters know who you are</p>
                </div>
              ) : (
                personas.map((p) => (
                  <div
                    key={p.id}
                    className={`p-3 rounded-xl border ${
                      p.is_default
                        ? 'bg-[#E11D48]/10 border-[#E11D48]/30'
                        : 'bg-[#1A1D24] border-white/5'
                    }`}
                    data-testid={`persona-card-${p.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-full bg-[#1A1D24] border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {p.avatar ? (
                          <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-[#71717A]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-bold text-white text-sm">{p.name}</span>
                          {p.is_default && (
                            <span className="px-1.5 py-0.5 text-[9px] bg-[#E11D48]/30 text-[#E11D48] rounded-full font-medium">Default</span>
                          )}
                        </div>
                        {p.gender && <p className="text-[11px] text-[#F59E0B] mb-1">{p.gender}</p>}
                        {p.description && (
                          <p className="text-xs text-[#A1A1AA] line-clamp-2">{p.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2.5 ml-14">
                      {p.is_default ? (
                        <button
                          onClick={() => removeDefault(p.id)}
                          className="px-3 py-1.5 text-xs font-medium text-[#E11D48] bg-[#E11D48]/10 border border-[#E11D48]/20 rounded-full hover:bg-[#E11D48]/20"
                          data-testid={`remove-persona-${p.id}`}
                        >
                          Remove
                        </button>
                      ) : (
                        <button
                          onClick={() => setDefaultPersona(p.id)}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-[#E11D48] rounded-full hover:bg-[#BE123C]"
                          data-testid={`use-persona-${p.id}`}
                        >
                          Use
                        </button>
                      )}
                      <button
                        onClick={() => deletePersona(p.id)}
                        className="px-3 py-1.5 text-xs font-medium text-[#71717A] hover:text-[#E11D48] border border-white/10 rounded-full hover:border-[#E11D48]/30"
                        data-testid={`delete-persona-${p.id}`}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Memory Vault paywall — shown for Free users when they tap the BookHeart icon */}
      {showMemoryPaywall && (
        <div
          className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowMemoryPaywall(false)}
          data-testid="memory-paywall-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md bg-gradient-to-br from-[#1B0E2E] via-[#2A0E1F] to-[#0A0A0A] border border-[#E11D48]/30 rounded-t-3xl sm:rounded-3xl p-6 relative overflow-hidden"
          >
            <div className="absolute -top-20 -right-20 w-48 h-48 bg-[#E11D48]/20 blur-3xl rounded-full pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-[#F59E0B]/15 blur-3xl rounded-full pointer-events-none" />

            <button
              onClick={() => setShowMemoryPaywall(false)}
              className="absolute top-3 right-3 p-1.5 rounded-full bg-white/5 text-white/60 hover:text-white hover:bg-white/10 transition-colors z-10"
              data-testid="memory-paywall-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E11D48] to-[#9F1239] flex items-center justify-center mb-4 shadow-lg shadow-[#E11D48]/30">
                <BookHeart className="w-7 h-7 text-white" />
              </div>

              <h3 className="font-['Unbounded'] text-xl text-white mb-1.5 leading-tight">
                Make them remember.<br />Forever.
              </h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed mb-5">
                The Memory Vault lets your characters remember everything about you across chats — your name, your secrets, your inside jokes. Available only on Premium & Ultimate.
              </p>

              <div className="space-y-2 mb-5">
                <div className="p-3 rounded-xl bg-gradient-to-r from-[#7C2D12]/40 to-[#F59E0B]/10 border border-[#F59E0B]/30">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <Crown className="w-3.5 h-3.5 text-[#F59E0B]" />
                      <span className="text-xs font-bold text-white">Premium</span>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-[#F59E0B] font-bold">Basic</span>
                  </div>
                  <p className="text-[11px] text-[#FCD34D]/90">Up to 5 permanent memories</p>
                </div>
                <div className="p-3 rounded-xl bg-gradient-to-r from-[#4C1D95]/40 to-[#E11D48]/15 border border-[#E11D48]/40">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-[#FDA4AF]" />
                      <span className="text-xs font-bold text-white">Ultimate</span>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-[#FDA4AF] font-bold">Flawless</span>
                  </div>
                  <p className="text-[11px] text-[#FECDD3]/90">Unlimited memories • Best extraction AI • 3x deeper per turn</p>
                </div>
              </div>

              <button
                onClick={() => { setShowMemoryPaywall(false); navigate('/profile'); }}
                className="w-full py-3 rounded-full bg-gradient-to-r from-[#FECDD3] via-[#F472B6] to-[#E11D48] text-[#2A0314] font-bold text-sm hover:scale-[1.02] transition-transform shadow-lg shadow-[#E11D48]/30"
                data-testid="memory-paywall-upgrade-btn"
              >
                Unlock Permanent Memory
              </button>
              <button
                onClick={() => setShowMemoryPaywall(false)}
                className="w-full mt-2 py-2 text-xs text-white/50 hover:text-white/80 transition-colors"
                data-testid="memory-paywall-later-btn"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
